<template>
  <div class="content-bg-color">
    <div class="container-fluid content-pad content-pad--big-top">
      <div class="row">
        <div class="col">
          <dx-back-text-button
            :text="$t('toTariffs')"
            is-external-link
            :external-link="tariffOverviewLink"
            inverted
            class="outer-container-spacing"
          />
        </div>
      </div>
      <h1 class="headlines text-center headline-offer">
        {{ $t('offer.claim1') }}:
        <br />
        {{ $t('offer.claim2') }}
      </h1>
      <dx-tariff-container :tariff="activeTariff" class="mt-5">
        <!-- FORM -->
        <template slot="content">
          <div class="row align-items-center">
            <div class="col-12 col-md-8 offset-md-2">
              <p>{{ $t('offer.answerQuestions') }}</p>
              <dx-private-basic-questions
                v-if="tariffType === 'private'"
                ref="privateIntroForm"
                editable
                @change="
                  () => {
                    hasChanged = true
                  }
                "
              />
              <dx-business-basic-questions
                v-else-if="tariffType === 'business'"
                ref="businessIntroForm"
                editable
                @change="
                  () => {
                    hasChanged = true
                  }
                "
              />
              <dx-business-move-basic-questions
                v-else-if="tariffType === 'b-move'"
                ref="businessMoveIntroForm"
                editable
                @change="
                  () => {
                    hasChanged = true
                  }
                "
              />
              <a v-else :href="tariffURL" class="secondary-link">
                {{ $t('offer.chooseProduct') }}
              </a>
            </div>

            <div class="col-12 text-right py-md-4 px-md-5 mt-3">
              <dx-button
                :text="$t('offer.calculatePremium')"
                class="dx-button--position"
                @click="saveForm"
              />
            </div>
          </div>
        </template>
      </dx-tariff-container>

      <!-- PRICE BOX -->
      <dx-loading-overlay v-if="loading" />
      <div
        v-else-if="!hasError && !hasChanged && premia.insurable !== null"
        class="premium-wrap shadow-container mt-5"
      >
        <div class="row">
          <div class="col-12 premium-head text-center py-5">
            <h2 class="mb-0">
              {{ $t('offer.yearlyPremium') }}
            </h2>
          </div>
        </div>
        <div class="row mt-4">
          <div v-if="premia.insurable" class="col-12 col-md-8 offset-md-2 py-3">
            <div
              v-for="(premium, index) in premiaComponents"
              :key="`premium-${index}`"
              class="row"
            >
              <div class="col-6 col-md-4">
                <h5>
                  {{ $t(`offer.premiumLabel.${premium.label}`) }}
                </h5>
              </div>
              <div class="col-6 col-md-8">
                {{
                  `${premium.amount.currency || 'CHF'} ${formatCurrency(
                    premium.amount.value,
                  )}`
                }}
              </div>
            </div>
            <div class="row mt-4">
              <div class="col-6 col-md-4">
                <h5 class="mb-0">
                  {{ $t('offer.total') }}
                </h5>
                <p>
                  {{ $t('offer.tax') }}
                </p>
              </div>
              <div class="col-6 col-md-8">
                {{ premia.totalPremium.currency || 'CHF' }}
                <span class="big-price">
                  {{ formatCurrency(premia.totalPremium.value) }}
                </span>
              </div>
            </div>
          </div>
          <div v-else class="col-12 col-md-8 offset-md-2 py-3">
            {{ $t('offer.uninsurable') }}
          </div>
          <div class="col-12 text-right py-md-4 px-md-5 mt-3">
            <dx-button
              :text="$t('offer.nextStep')"
              class="dx-button--position"
              @click="goForward"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import querystring from 'querystring'
import {
  DxButton,
  DxBackTextButton,
} from '@sumcumo/dextra-frontend-component-lib-old'
import Cookies from 'js-cookie'
import DxTariffContainer from '~/components/DX-TariffContainer'
import DxPrivateBasicQuestions from '~/components/offer/DX-PrivateBasicQuestions'
import DxBusinessBasicQuestions from '~/components/offer/DX-BusinessBasicQuestions'
import DxBusinessMoveBasicQuestions from '~/components/offer/DX-BusinessMoveBasicQuestions'
import DxLoadingOverlay from '~/components/DX-LoadingOverlay'
import scrollFirstErrorIntoView, {
  scrollIntoView,
} from '~/javascripts/helper/scollIntoView'
import validationErrorsMixin from '~/javascripts/mixins/validationErrorsMixin'
import { RESET_STORE } from '~/domains/application/__store__/actions'
import { trackPageRoute } from '~/javascripts/helper/webtrekk'
import { formatCurrency } from '~/javascripts/utils'

export default {
  name: 'DxOfferIntro',
  components: {
    DxTariffContainer,
    DxPrivateBasicQuestions,
    DxBusinessBasicQuestions,
    DxBusinessMoveBasicQuestions,
    DxLoadingOverlay,
    DxButton,
    DxBackTextButton,
  },
  mixins: [validationErrorsMixin],
  props: {
    product: {
      type: String,
      default: 'p-private',
    },
  },
  data() {
    return {
      hasError: false,
      hasChanged: false,
      scrollIntoView,
      tariffURL: `${process.env.DEXTRA_WEBSITE_URL}${this.$t(
        'navLinksTariffs.privatLegal',
      )}`,
      formatCurrency,
    }
  },
  head() {
    return {
      meta: [
        {
          name: 'robots',
          content: 'noindex,follow',
        },
      ],
    }
  },
  computed: {
    loading() {
      return this.$store.state.application.loading
    },
    activeTariff() {
      return this.$store.state.offer.activeTariff
    },
    premia() {
      return this.$store.state.offer.premia
    },
    premiaComponents() {
      return this.premia.components.filter((cmp) => cmp.amount.value > 0)
    },
    tariffType() {
      return this.$store.getters.GET_ACTIVE_TARIFF_TYPE
    },
    formRef() {
      let ref = 'smallBusinessIntroForm'
      if (this.tariffType === 'private') {
        ref = 'privateIntroForm'
      } else if (this.tariffType === 'business') {
        ref = 'businessIntroForm'
      } else {
        ref = 'businessMoveIntroForm'
      }
      return ref
    },
    tariffOverviewLink() {
      if (this.tariffType === 'private') {
        return `${process.env.DEXTRA_WEBSITE_URL}${this.$t(
          'navLinksTariffs.privatLegal',
        )}`
      }
      return `${process.env.DEXTRA_WEBSITE_URL}${this.$t(
        'navLinksTariffs.companyProtection',
      )}`
    },
  },
  mounted() {
    const { reset, logout, reason } = this.$route.query
    if (logout) {
      this.$store.dispatch(RESET_STORE)
      this.$apolloProvider.defaultClient.resetStore()
      if (reason === 'sessionExpired') {
        this.$notification.error({
          messageKey: 'loginPage.errors.sessionExpired',
        })
      }
    }
    if (this.activeTariff !== this.product || reset || logout) {
      this.$store.commit('RESET_STORE_OFFER')
      this.$store.commit('SET_ACTIVE_TARIFF', this.product)
      Vue.nextTick(() => this.$refs[this.formRef].resetForm())

      // delete reset query so user can reload page without deleting again
      if (reset) {
        const query = { ...this.$route.query }
        delete query.reset
        const queryString = querystring.encode(query)
        // eslint-disable-next-line no-restricted-globals
        history.replaceState(
          {},
          null,
          queryString ? `?${queryString}` : window.location.pathname,
        )
      }
    }
    if (this.$route.query.pid) {
      Cookies.set(
        'broker',
        {
          broker_id: this.$route.query.pid,
          origin: this.$route.query.o,
        },
        { expires: 30, domain: process.env.DEXTRA_COOKIE_DOMAIN },
      )
    }
  },
  methods: {
    async saveForm() {
      const valid = await this.$refs[this.formRef].validateForm()
      if (valid) {
        const result = await this.calcPremia()
        if (result) {
          this.$refs[this.formRef].saveForm()
          setTimeout(() => {
            this.scrollIntoView(document.querySelector('.premium-wrap'))
          }, 10)
          const contentIdPrefix =
            this.activeTariff[0] === 'p'
              ? 'privatrechtsschutz'
              : 'unternehmensrechtsschutz'
          trackPageRoute({
            metaData: {
              trackingId: `dextra.${contentIdPrefix}.${this.activeTariff}.antragsstrecke.jahrespraemie`,
            },
            locale: this.$i18n.locale,
            tariffType: this.tariffType,
          })
        } else {
          scrollFirstErrorIntoView()
        }
      } else {
        scrollFirstErrorIntoView()
      }
    },
    async calcPremia() {
      const actionKey = this.activeTariff.split('-').join('').toUpperCase()
      const action = `CALC_${actionKey}_PREMIA`
      const payload = this.$refs[this.formRef].formData
      const result = await this.$store.dispatch(action, payload)
      if (result) {
        const errors = result.validationErrors
        if (errors.length) {
          this.hasError = true
          errors.forEach((err) => {
            this.$refs[this.formRef].$validator.errors.add(
              this.getServerValidationError(err),
            )
          })
          return false
        }
        this.hasError = false
        this.hasChanged = false
        return true
      }
      // OR NETWORK ERROR:
      this.hasError = true
      this.$notification.error({ messageKey: 'notifications.error.message' })
      return false
    },
    async goForward() {
      this.$store.commit('SET_STEP', 2)
      this.$router.push({ path: this.localePath('Offer-PolicyholderData') })
    },
  },
}
</script>

<style lang="scss" scoped>
@import 'index';
</style>
